import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import MembershipCard from "../../components/cards/MembershipCard";
import Footer from "../../components/footer";
import Header from "../../components/header";
import { useTranslate } from "../../context/TranslationContext";
import i18n from "../../i18n";
import { useCheckPrivateAccessQuery } from "../../redux/Apis/PackageAccess";
import {
  useGetCurrentSubscriptionQuery,
  useGetPackageQuery,
  useSubscribeMutation,
} from "../../redux/Apis/Packages";
import { setLanguage } from "../../redux/Slices/Language";
import { setMetaTags } from "../../utils/helper";
import { getConfigByCountryCode } from "../../utils/localization";

const PrivateMembership = () => {
  const lang = useSelector((state) => state.language.lang);
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn, user } = useSelector((state) => state.authSlice);
  const [memberships, setMemberships] = useState([]);
  const dispatch = useDispatch();
  const { t } = useTranslate("membership");

  const {
    data: accessData,
    error: accessError,
    isLoading: accessLoading,
    isSuccess: accessSuccess,
  } = useCheckPrivateAccessQuery({ userid: user?._id }, { skip: !isLoggedIn });
  console.log("accessData", accessData, accessLoading, accessSuccess);
  useEffect(() => {
    const countryCode =
      user?.country || localStorage?.getItem("selectedCountry") || "gb";

    const { lang } = getConfigByCountryCode(countryCode);

    dispatch(setLanguage(lang));

    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [user?.country, dispatch]);

  const {
    data: packageData,
    isLoading: packageLoading,
    isSuccess: packageSuccess,
    refetch: refetchPackages,
  } = useGetPackageQuery(
    { sortBy: "price", lang, isprivate: "true" },
    { skip: !accessData?.url },
  );

  const { data: subscriptionData, refetch: refetchSubscription } =
    useGetCurrentSubscriptionQuery({ lang }, { skip: !isLoggedIn });

  const [
    subscribe,
    {
      data: subscribeData,
      isSuccess: subscribeSuccess,
      isLoading: subscribeLoading,
    },
  ] = useSubscribeMutation();

  useEffect(() => {
    setMetaTags(
      "Traineego | Buy or Create Automated Courses & Tracks for Fitness & Wellness",
      "Create, manage, and sell your own courses and tracks, or buy high-quality content. Empower your career through automation and passive income today!",
    );
  }, []);

  useEffect(() => {
    if (accessData?.url) {
      refetchPackages(); // only when query is active
    }

    if (isLoggedIn) {
      refetchSubscription();
    }
  }, [isLoggedIn, accessData?.url, refetchPackages, refetchSubscription]);

  useEffect(() => {
    if (subscribeSuccess && subscribeData?.data) {
      window.location.href = subscribeData.data;
    }
  }, [subscribeSuccess, subscribeData]);

  useEffect(() => {
    if (packageSuccess && packageData?.data) {
      const currentPackageId = subscriptionData?.data?.package?._id;
      const updatedMemberships = packageData.data.map((item) => {
        const isCurrent = item._id === currentPackageId;
        return {
          ...item,
          button: isCurrent ? "Current" : t("buyNow"),
          button_disabled: isCurrent,
        };
      });
      setMemberships(updatedMemberships);
    }
  }, [packageSuccess, packageData, subscriptionData, t]);

  const handleSubscribe = async (id) => {
    if (!isLoggedIn) {
      navigate("/auth/login", {
        replace: true,
        state: { from: location },
      });
      return;
    }

    try {
      await subscribe({ id, params: { lang } }).unwrap();
    } catch (err) {
      console.error(err);

      toast.error(err?.data?.message || err?.error);
    }
  };

  return (
    <>
      <Header />
      {/* <Loader loading={isLoading || funnelLoading} /> */}

      {!isLoggedIn ? (
        <div className="banner mnh-0 back-transparent">
          <h4>Please log in to view this content.</h4>
        </div>
      ) : accessLoading ? (
        <div className="text-center py-5">
          <Spinner />
        </div>
      ) : accessSuccess && accessData?.url ? (
        <>
          <section className="banner mnh-0 back-transparent">
            <div className="container">
              <div className="row justify-content-center align-items-center">
                <div className="col-md-8 text-center">
                  <h2 className="text-uppercase">{t("bannerTitle")}</h2>
                  <p className="mb-0 p-lg l">{t("bannerSubtitle")}</p>
                </div>
              </div>
            </div>
          </section>

          <section className="packages mb-4">
            <div className="container">
              <h5 className="text-uppercase mb-5 text-center">
                {t("choosetitle")} <br /> {t("startcreating")}
              </h5>
              {packageLoading ? (
                <div className="text-center">
                  <Spinner />
                </div>
              ) : (
                <div className="table-responsive pricing-table">
                  <div className="row d-flex m-0 h-100">
                    {memberships.map((plan, index) => (
                      <MembershipCard
                        key={plan._id}
                        index={index}
                        data={plan}
                        onSubscriptionBuy={handleSubscribe}
                        isLoading={subscribeLoading}
                        selectedCountry={
                          user?.country ||
                          localStorage?.getItem("selectedCountry") ||
                          "gb"
                        }
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        </>
      ) : (
        // ❌ NO ACCESS
        <div
          className="d-flex justify-content-center align-items-center text-center"
          style={{ minHeight: "300px" }}
        >
          <h4>
            {accessData?.message ||
              accessError?.data?.message ||
              "You do not have access"}
          </h4>
        </div>
      )}

      <Footer />
    </>
  );
};

export default PrivateMembership;
